import React from "react";
import { Box, Stepper, Step, StepLabel, Container, Fade } from "@mui/material";
import { OnBoardingProvider } from "./OnBoardingPage/OnBoardingContext";
import { OnBoardingStep1 } from "./OnBoardingPage/OnBoardingStep1";
import { OnBoardingStep2 } from "./OnBoardingPage/OnBoardingStep2";
import { OnBoardingStep3 } from "./OnBoardingPage/OnBoardingStep3";
import { useHandleCompleteOnboarding } from "../../hooks";
import { StandardizedOnboardingData } from "./OnBoardingPage/utils/onboardingDataTransformer";
import { useTranslation } from "react-i18next";
import { messages } from "../../locales/messages";

const OnBoardingContent: React.FC = () => {
  const { t } = useTranslation();
  const [step, setStep] = React.useState(1);
  const [standardizedData, setStandardizedData] =
    React.useState<StandardizedOnboardingData | null>(null);
  const handleCompleteOnboarding = useHandleCompleteOnboarding();

  const steps = [
    t(messages.Find_Your_Store()) as string,
    t(messages.Store_Details()) as string,
    t(messages.Complete_Setup()) as string,
  ];

  const handleStepTwoComplete = (data: StandardizedOnboardingData) => {
    setStandardizedData(data);
    setStep(3);
  };

  const handleCompleteSetup = async (
    standardizedData: StandardizedOnboardingData
  ) => {
    await handleCompleteOnboarding(standardizedData, {
      redirectPath: "/dashboard",
      onSuccess: () => {
        console.log("Onboarding completed successfully!");
      },
      onError: (error) => {
        console.error("Error completing onboarding:", error);
      },
    });
  };

  return (
    <Box
      minHeight="calc(100vh - 65px)"
      bgcolor="linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)"
      mt="65px"
      sx={{
        background: "linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)",
      }}
    >
      <Container maxWidth="lg" sx={{ pt: 4, pb: 2 }}>
        <Stepper
          activeStep={step - 1}
          sx={{
            mb: 4,
            "& .MuiStepLabel-root .Mui-completed": {
              color: "#f58321",
            },
            "& .MuiStepLabel-root .Mui-active": {
              color: "#f58321",
            },
            "& .MuiStepConnector-line": {
              borderColor: "#e0e0e0",
            },
            "& .Mui-completed .MuiStepConnector-line": {
              borderColor: "#f58321",
            },
            "& .Mui-active .MuiStepConnector-line": {
              borderColor: "#f58321",
            },
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel
                sx={{
                  "& .MuiStepLabel-label": {
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    fontWeight: 500,
                  },
                  "& .MuiStepLabel-label.Mui-active": {
                    color: "#f58321",
                    fontWeight: 600,
                  },
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Container>
      {/* Step Content */}
      <Container maxWidth="lg" sx={{ pb: 4 }}>
        {" "}
        <Fade in={true} timeout={500}>
          <Box>
            {step === 1 && <OnBoardingStep1 onContinue={() => setStep(2)} />}
            {step === 2 && (
              <OnBoardingStep2
                onContinue={handleStepTwoComplete}
                onBack={() => setStep(1)}
              />
            )}
            {step === 3 && (
              <OnBoardingStep3
                onContinue={handleCompleteSetup}
                onBack={() => setStep(2)}
                onEdit={() => setStep(2)}
                standardizedData={standardizedData}
              />
            )}
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

const OnBoardingPage: React.FC = () => {
  return (
    <OnBoardingProvider>
      <OnBoardingContent />
    </OnBoardingProvider>
  );
};

export default OnBoardingPage;
